import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Space } from 'antd';
import { TriangleAlert } from 'lucide-react';
import Button from '../../main/components/Button';
import Modal from '../../main/components/Modal';
import GlobalContext from '../../main/utils/GlobalContext';

// Bulk anonymise confirmation: warning copy, one checkbox per user group, password re-entry.
// Render it inside the parent Modal so headlessui keeps focus within it.
const ModalAnonymiseUsers = ({
  visible,
  title,
  groups,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const { locales } = useContext(GlobalContext);
  const [password, setPassword] = useState('');
  const [selected, setSelected] = useState(groups.map((group) => group.key));
  const [errors, setErrors] = useState({});

  // The parent settings modal stays mounted between opens, so clear the typed
  // password and restore the default selection whenever this dialog closes
  useEffect(() => {
    if (!visible) {
      setPassword('');
      setSelected(groups.map((group) => group.key));
      setErrors({});
    }
  }, [visible]);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setErrors({});
  };

  const toggleGroup = (key) => {
    setSelected((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key],
    );
    setErrors({});
  };

  const handleAnonymise = () => {
    if (selected.length === 0) {
      setErrors({ groups: locales.admin.selectGroupToAnonymise });
      return;
    }
    if (!password) {
      setErrors({ password: 'Password is required' });
      return;
    }

    onConfirm(password, selected);
  };

  return (
    <Modal isOpen={visible} onClose={onClose} title={title}>
      <div className="confirmation-modal-delete">
        <Form layout="vertical">
          <div className="flex flex-col gap-1 mb-4">
            <p className="text-red-500 font-bold flex items-center gap-2">
              <TriangleAlert className="w-4 h-4" />
              {locales.admin.important}
            </p>
            <p>{locales.admin.confirmationAnonymiseUsersWarningModal}</p>
          </div>

          <Form.Item
            label={locales.admin.anonymiseGroupsLabel}
            className="admin-input-label"
            validateStatus={errors.groups ? 'error' : ''}
            help={errors.groups}
          >
            <div className="flex flex-col">
              {groups.map((group) => (
                <label
                  key={group.key}
                  htmlFor={`anonymise-group-${group.key}`}
                  className="flex cursor-pointer select-none items-center gap-2.5 py-1"
                >
                  <input
                    id={`anonymise-group-${group.key}`}
                    type="checkbox"
                    checked={selected.includes(group.key)}
                    onChange={() => toggleGroup(group.key)}
                  />
                  <span>{group.label}</span>
                </label>
              ))}
            </div>
          </Form.Item>

          <Form.Item
            label={locales.account.passwordConfirmationLabel}
            className="admin-input-label"
            validateStatus={errors.password ? 'error' : ''}
            help={errors.password}
          >
            <Input.Password
              placeholder={locales.account.passwordPlaceholder}
              className="admin-input"
              value={password}
              onChange={handlePasswordChange}
            />
          </Form.Item>
          <Space>
            <Button variant="outline" onClick={onClose}>
              {locales.common.cancel}
            </Button>
            <Button
              variant="destructiveOutline"
              disabled={loading}
              onClick={handleAnonymise}
            >
              {locales.admin.anonymiseAllUsers}
            </Button>
          </Space>
        </Form>
      </div>
    </Modal>
  );
};

ModalAnonymiseUsers.propTypes = {
  visible: PropTypes.bool,
  title: PropTypes.string,
  groups: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string }),
  ).isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default ModalAnonymiseUsers;
